/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, {
  AxiosInstance,
  AxiosResponse,
  AxiosError,
  AxiosRequestConfig,
} from "axios";
import { logger } from "../utils/logger";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000/api";

export interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  skipAuthRedirect?: boolean;
  skipErrorDialog?: boolean;
}

export interface ApiConfig {
  onSuccess?: (response: AxiosResponse) => void;
  onError?: (error: AxiosError) => void;
  onTokenExpiry?: () => void;
}

export class HttpClient {
  private instance: AxiosInstance;
  private config: ApiConfig;
  private errorListener: ((error: any) => void) | null = null;
  private maintenanceListener: (() => void) | null = null;

  constructor(config: ApiConfig = {}) {
    this.config = config;
    this.instance = axios.create({
      baseURL: API_URL,
      withCredentials: true,
      timeout: 30000,
      headers: {
        "Content-Type": "application/json",
      },
    });

    this.initializeInterceptors();
  }

  public setErrorListener(listener: (error: any) => void) {
    this.errorListener = listener;
  }

  public setMaintenanceListener(listener: () => void) {
    this.maintenanceListener = listener;
  }

  private initializeInterceptors() {
    this.instance.interceptors.response.use(
      (response) => {
        const payload: any = response?.data;
        if (
          payload &&
          typeof payload === "object" &&
          "success" in payload &&
          "data" in payload
        ) {
          return { ...response, data: payload.data };
        }
        if (this.config.onSuccess) {
          this.config.onSuccess(response);
        }
        return response;
      },
      (error: AxiosError) => {
        const config = error.config as CustomAxiosRequestConfig;

        if (
          error.code === "ECONNABORTED" ||
          error.message?.includes("timeout")
        ) {
          error.message = "Connection timed out. Please try again.";
        }

        if (error.response?.status === 503) {
          if (this.maintenanceListener) {
            this.maintenanceListener();
          }
          return Promise.reject(error);
        }

        if (error.response?.status === 401) {
          if (!config?.skipAuthRedirect && this.config.onTokenExpiry) {
            this.config.onTokenExpiry();
          }
        } else {
          if (this.errorListener && !config?.skipErrorDialog) {
            this.errorListener(error);
          } else if (this.config.onError) {
            this.config.onError(error);
          }
        }
        return Promise.reject(error);
      },
    );
  }

  public async request<T = any>(
    config: CustomAxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    return this.instance.request<T>(config);
  }
}

const httpClient = new HttpClient({
  onTokenExpiry: () => {
    window.location.href = "/login";
  },
  onError: (error) => {
    logger.error("API Error:", error);
  },
});

export const api = {
  request: <T = any>(config: CustomAxiosRequestConfig) =>
    httpClient.request<T>(config),
};

export default httpClient;
